
import React, { useState, useEffect } from 'react';
import { Coin } from '../types.ts';
import { fetchCoinHistory } from '../services/cryptoApi.ts';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

interface CoinDetailProps {
  coin: Coin;
  onBack: () => void;
}

const CoinDetail: React.FC<CoinDetailProps> = ({ coin, onBack }) => {
  const [history, setHistory] = useState<any[]>([]);
  const [timeframe, setTimeframe] = useState('1D');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadHistory = async () => {
      setLoading(true);
      const days = timeframe === '1D' ? '1' : timeframe === '1W' ? '7' : timeframe === '1M' ? '30' : timeframe === '1Y' ? '365' : 'max';
      const data = await fetchCoinHistory(coin.id, days);
      setHistory(data);
      setLoading(false);
    };
    loadHistory();
  }, [coin.id, timeframe]);

  const timeframes = ['1D', '1W', '1M', '1Y', 'All'];

  return (
    <div className="h-full flex flex-col bg-[#0D0D0D] animate-in slide-in-from-right duration-300">
      {/* Top Header */}
      <div className="px-5 pt-12 pb-6 flex justify-between items-center bg-black/40 backdrop-blur-xl sticky top-0 z-10">
        <button onClick={onBack} className="p-3 bg-zinc-900/80 rounded-full active:scale-90 transition-transform">
           <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" /></svg>
        </button>
        <div className="flex items-center space-x-3">
            <img src={coin.image} className="w-8 h-8 rounded-full shadow-lg" alt="" />
            <div className="flex flex-col">
              <h2 className="text-white font-bold text-base leading-none">{coin.name}</h2>
              <span className="text-zinc-500 font-bold text-[10px] uppercase tracking-widest">{coin.symbol}</span>
            </div>
        </div>
        <button className="p-3 bg-zinc-900/80 rounded-full active:scale-90 transition-transform">
           <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.175 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.382-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
        </button>
      </div>

      <div className="px-6 mt-6 space-y-1">
        <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest">Current Price</p>
        <div className="flex items-baseline space-x-3">
          <h1 className="text-white text-5xl font-bold tracking-tight">${coin.current_price.toLocaleString(undefined, { minimumFractionDigits: 2 })}</h1>
          <div className={`flex items-center space-x-1 font-bold text-sm ${coin.price_change_percentage_24h >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
             <span>{coin.price_change_percentage_24h >= 0 ? '↑' : '↓'}</span>
             <span>{Math.abs(coin.price_change_percentage_24h).toFixed(2)}%</span>
          </div>
        </div>
      </div>

      {/* Main Chart Area */}
      <div className="flex-1 mt-10 min-h-[280px] relative px-0">
        {loading ? (
            <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-10 h-10 border-3 border-blue-500/30 border-t-blue-500 rounded-full animate-spin"></div>
            </div>
        ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorVal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#18181b', border: 'none', borderRadius: '16px', fontSize: '14px', color: '#fff' }}
                  itemStyle={{ color: '#fff', fontWeight: 'bold' }}
                  labelStyle={{ display: 'none' }}
                  cursor={{ stroke: '#3B82F6', strokeWidth: 1 }}
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#3B82F6" 
                  strokeWidth={3} 
                  fillOpacity={1} 
                  fill="url(#colorVal)" 
                  animationDuration={800}
                />
                <XAxis hide dataKey="time" />
                <YAxis hide domain={['auto', 'auto']} />
              </AreaChart>
            </ResponsiveContainer>
        )}
      </div>

      {/* Market Data Grid */}
      <div className="px-6 grid grid-cols-2 gap-3 mt-4">
        <div className="bg-zinc-900/40 p-5 rounded-3xl border border-zinc-800/50">
          <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">Market Cap</p>
          <p className="text-white font-bold text-base">${(coin.market_cap / 1e9).toFixed(1)}B</p>
        </div>
        <div className="bg-zinc-900/40 p-5 rounded-3xl border border-zinc-800/50">
          <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-widest mb-1">Volume 24h</p>
          <p className="text-white font-bold text-base">#1.2B</p>
        </div>
      </div>

      {/* Timeframe Selector */}
      <div className="px-6 mt-8">
        <div className="flex bg-zinc-900/60 backdrop-blur-md rounded-2xl p-1.5 border border-zinc-800">
          {timeframes.map((tf) => (
            <button
              key={tf}
              onClick={() => setTimeframe(tf)}
              className={`flex-1 py-2 text-xs font-bold rounded-xl transition-all ${
                timeframe === tf ? 'bg-zinc-800 text-white shadow-lg border border-zinc-700/50' : 'text-zinc-500'
              }`}
            >
              {tf}
            </button>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="px-6 pt-10 pb-12 flex space-x-4 bg-gradient-to-t from-black to-transparent">
          <button className="flex-1 py-5 bg-zinc-900 text-white font-bold rounded-2xl border border-zinc-800 active:scale-95 transition-all text-xs uppercase tracking-widest">
              Sell
          </button>
          <button className="flex-1 py-5 bg-blue-600 text-white font-bold rounded-2xl shadow-xl shadow-blue-500/20 active:scale-95 transition-all text-xs uppercase tracking-widest">
              Buy Now
          </button>
      </div>
    </div>
  );
};

export default CoinDetail;
